import { useLayoutEffect, useRef } from 'react';
import type { Editor } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { readableInkKey } from './readableInk';

type Color = number[];
const INK_ATTRIBUTE = 'data-rich-text-readable-ink';
const INK_PROPERTY = '--rich-text-readable-ink';
const luminance = (color: Color) => color.slice(0, 3).reduce((sum, channel, i) => {
  const value = channel / 255;
  return sum + (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4) * [0.2126, 0.7152, 0.0722][i];
}, 0);
const contrast = (foreground: Color, background: Color) => {
  const a = luminance(foreground), b = luminance(background);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};
const composite = (foreground: Color, background: Color) => foreground.slice(0, 3).map(
  (channel, i) => channel * foreground[3] / 255 + background[i] * (1 - foreground[3] / 255),
);

/** Adapt screen ink to its actual surface, leaving the editor's authored colors intact. */
export function useReadableRichText(editor?: Editor | null) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const context = canvas.getContext('2d');
    if (!context) return;
    const color = (value: string): Color => {
      context.clearRect(0, 0, 1, 1);
      context.fillStyle = value;
      context.fillRect(0, 0, 1, 1);
      return Array.from(context.getImageData(0, 0, 1, 1).data);
    };
    let frame = 0;
    const update = () => {
      // Let ProseMirror remove its decorations before measuring original editor ink.
      if (editor && !editor.isDestroyed) {
        editor.view.dispatch(editor.state.tr.setMeta(readableInkKey, DecorationSet.empty));
      } else {
        for (const element of root.querySelectorAll<HTMLElement>(`[${INK_ATTRIBUTE}]`)) {
          element.removeAttribute(INK_ATTRIBUTE);
          element.style.removeProperty(INK_PROPERTY);
        }
      }
      const documents = root.matches('.rich-text-document') ? [root] : Array.from(root.querySelectorAll<HTMLElement>('.rich-text-document'));
      const changes: Array<[HTMLElement, Color]> = [];
      const backgrounds = new Map<HTMLElement, Color>();
      const background = (element: HTMLElement): Color => {
        const cached = backgrounds.get(element);
        if (cached) return cached;
        const underneath = element.parentElement ? background(element.parentElement) : [255, 255, 255];
        const value = composite(color(getComputedStyle(element).backgroundColor), underneath);
        backgrounds.set(element, value);
        return value;
      };
      for (const doc of documents) {
        const themeInk = color(getComputedStyle(doc).color);
        const leaves = new Set<HTMLElement>();
        const walker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.textContent?.trim() && node.parentElement) leaves.add(node.parentElement);
        }
        for (const element of leaves) {
          const surface = background(element);
          const authored = composite(color(getComputedStyle(element).color), surface);
          if (contrast(authored, surface) >= 4.5) continue;
          // Neutral body text should match theme ink, rather than stop at a barely readable gray.
          // Highlights may need the opposite ink; colored text keeps its hue through blending.
          const target = contrast(themeInk, surface) >= 4.6 ? themeInk :
            contrast([255, 255, 255], surface) > contrast([0, 0, 0], surface) ? [255, 255, 255] : [0, 0, 0];
          if (Math.max(...authored) - Math.min(...authored) <= 40) {
            changes.push([element, target.slice(0, 3)]);
            continue;
          }
          const blend = (amount: number) => authored.map((channel, i) => Math.round(channel + (target[i] - channel) * amount));
          let low = 0, high = 1;
          for (let i = 0; i < 12; i++) {
            const middle = (low + high) / 2;
            if (contrast(blend(middle), surface) >= 4.6) high = middle; else low = middle;
          }
          changes.push([element, blend(high)]);
        }
      }
      const decorations: Decoration[] = [];
      for (const [element, ink] of changes) {
        const value = `rgb(${ink.join(', ')})`;
        if (editor && !editor.isDestroyed) {
          for (const node of element.childNodes) {
            if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) continue;
            const from = editor.view.posAtDOM(node, 0);
            decorations.push(Decoration.inline(from, from + node.textContent.length, {
              [INK_ATTRIBUTE]: '', style: `${INK_PROPERTY}: ${value}`,
            }));
          }
        } else {
          element.style.setProperty(INK_PROPERTY, value);
          element.setAttribute(INK_ATTRIBUTE, '');
        }
      }
      if (editor && !editor.isDestroyed) {
        editor.view.dispatch(editor.state.tr.setMeta(readableInkKey, DecorationSet.create(editor.state.doc, decorations)));
      }
      // Our CSS variable changes are not author edits and must not retrigger the observer.
      contentObserver.takeRecords();
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const contentObserver = new MutationObserver(records => { if (records.length) schedule(); });
    contentObserver.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['style', 'class'] });
    const themeObserver = new MutationObserver(schedule);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-layout', 'data-color-scheme', 'data-appearance', 'class'] });
    update();
    return () => { cancelAnimationFrame(frame); contentObserver.disconnect(); themeObserver.disconnect(); };
  }, [editor]);
  return ref;
}
